/**
 * Sponsor Enrollment Dashboard
 * Main dashboard for sponsor organizations to monitor enrollment metrics
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Users,
  TrendingUp,
  DollarSign,
  Building2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface SponsorEnrollmentDashboardProps {
  sponsorId: number;
  programId?: number;  // Optional program filter
}

interface EnrollmentMetrics {
  total_metrics: {
    total_enrollments: number;
    active_enrollments: number;
    pending_enrollments: number;
    work_compliance_rate: number | null;
    total_incentives_paid: number;
    avg_cost_per_enrollment: number;
  };
  by_state: Record<string, number>;
  by_cbo: Array<{
    cbo_id: number;
    cbo_name: string;
    enrollments: number;
    active: number;
  }>;
}

export const SponsorEnrollmentDashboard: React.FC<SponsorEnrollmentDashboardProps> = ({
  sponsorId,
  programId
}) => {
  const { t } = useAppTranslation();
  const [metrics, setMetrics] = useState<EnrollmentMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMetrics();
  }, [sponsorId, programId]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      let url = `/api/sponsors/${sponsorId}/enrollment-metrics`;
      if (programId) {
        url += `?program_id=${programId}`;
      }
      const response = await apiClient.get(url);
      setMetrics(response.data);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch metrics';
      setError(errorMsg);
      console.error('Error fetching sponsor metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error: {error}</div>;
  if (!metrics) return null;

  const { total_metrics, by_state, by_cbo } = metrics;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{t('sponsors.enrollment_and_engagement_dashboard', 'Enrollment and Engagement Dashboard')}</h2>
        <Badge variant="outline">Sponsor #{sponsorId}</Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('sponsors.total_enrollments', 'Total Enrollments')}</p>
              <p className="text-3xl font-bold mt-2">
                {total_metrics.total_enrollments.toLocaleString()}
              </p>
            </div>
            <Users className="w-12 h-12 text-blue-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('sponsors.active_enrollments', 'Active Enrollments')}</p>
              <p className="text-3xl font-bold mt-2">
                {total_metrics.active_enrollments.toLocaleString()}
              </p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('sponsors.compliance_rate', 'Compliance Rate')}</p>
              <p className="text-3xl font-bold mt-2">
                {total_metrics.work_compliance_rate !== null
                  ? `${total_metrics.work_compliance_rate.toFixed(1)}%`
                  : 'N/A'}
              </p>
            </div>
            <TrendingUp className="w-12 h-12 text-purple-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('sponsors.incentives_paid', 'Incentives Paid')}</p>
              <p className="text-3xl font-bold mt-2">
                ${total_metrics.total_incentives_paid.toLocaleString()}
              </p>
            </div>
            <DollarSign className="w-12 h-12 text-green-500" />
          </div>
        </Card>
      </div>

      {/* State Distribution */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{t('sponsors.enrollments_by_state', 'Enrollments by State')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(by_state).map(([state, count]) => (
            <div key={state} className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-sm text-gray-600">{state}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Top CBOs */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{t('sponsors.top_performing_cbos', 'Top Performing CBOs')}</h3>
        <div className="space-y-3">
          {by_cbo.slice(0, 5).map((cbo) => (
            <div
              key={cbo.cbo_id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium">{cbo.cbo_name}</p>
                  <p className="text-sm text-gray-600">
                    {cbo.active} active / {cbo.enrollments} total
                  </p>
                </div>
              </div>
              <Badge variant="outline">{cbo.enrollments} enrollments</Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* Average Cost Per Enrollment */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{t('sponsors.avg_cost_per_enrollment', 'Avg Cost per Enrollment')}</h3>
            <p className="text-3xl font-bold mt-2">
              ${total_metrics.avg_cost_per_enrollment.toFixed(2)}
            </p>
          </div>
          <TrendingUp className="w-12 h-12 text-blue-500" />
        </div>
      </Card>
    </div>
  );
};
